/**
 * Shared Rule Dialog — one dialog for creating/editing watch rules everywhere.
 *
 * Used by:
 *  - NotificationRulesPage (full mode: category → policy → details)
 *  - Strategy page (locked mode: formula + type pre-selected, condition-only)
 *
 * When `lockedRuleType` is set, the rule-type picker is hidden.
 * When `lockedFormulaId` is set, the formula picker is hidden.
 * When `conditionOnly` is set, only condition fields are shown.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Button, IconButton, TextField, Switch,
  CircularProgress, Collapse,
} from '@mui/material';
import {
  Close,
  ExpandMore,
  ExpandLess,
  ErrorOutline,
  WarningAmberOutlined,
  InfoOutlined,
  DashboardOutlined,
  EmailOutlined,
  WhatsApp,
  ArrowBack,
} from '@mui/icons-material';
import type { RuleTypeMeta, ConditionField } from '../../hooks/useWatchRules';

// ─── Constants ───────────────────────────────────────────────────────

export const SEVERITY_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  critical: { label: 'Critical', icon: <ErrorOutline sx={{ fontSize: 16, color: '#dc2626' }} />, color: '#dc2626', bg: '#fef2f2' },
  warning: { label: 'Warning', icon: <WarningAmberOutlined sx={{ fontSize: 16, color: '#d97706' }} />, color: '#d97706', bg: '#fffbeb' },
  info: { label: 'Info', icon: <InfoOutlined sx={{ fontSize: 16, color: '#2563eb' }} />, color: '#2563eb', bg: '#eff6ff' },
};

const CATEGORY_META: Record<string, { label: string; color: string; bg: string; desc: string }> = {
  strategy: { label: 'Strategy', color: '#2563eb', bg: '#eff6ff', desc: 'Coverage, buffer, logistics, exposure' },
  market:   { label: 'Market',   color: '#7c3aed', bg: '#f5f3ff', desc: 'Price targets, backwardation, volatility' },
  system:   { label: 'System',   color: '#0d9488', bg: '#f0fdfa', desc: 'Cost basis, M2M, hedge gaps' },
};

const CHANNEL_META: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined sx={{ fontSize: 15 }} /> },
  { key: 'email',     label: 'Email',     icon: <EmailOutlined sx={{ fontSize: 15 }} /> },
  { key: 'whatsapp',  label: 'WhatsApp',  icon: <WhatsApp sx={{ fontSize: 15 }} /> },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DEFAULT_MONTHLY = [100, 100, 100, 75, 75, 75, 50, 50, 50, 25, 25, 25];

export function policyLabel(ruleType: string): string {
  const map: Record<string, string> = {
    buffer_breach: 'Buffer',
    coverage_below_threshold: 'Coverage',
    coverage_low: 'Coverage',
    logistics_shortfall: 'Logistics',
    exposure_threshold: 'Exposure',
    cost_basis_drift: 'Cost Basis',
    hedge_coverage_gap: 'Hedge Gap',
    m2m_threshold: 'M2M',
    price_target: 'Price Target',
    backwardation: 'Backwardation',
    volatility_spike: 'Volatility',
  };
  return map[ruleType] || ruleType.replace(/_/g, ' ');
}

// ─── Section label ───────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{children}</p>
);

// ─── Condition Field Editor ──────────────────────────────────────────

const ConditionFieldEditor: React.FC<{
  field: ConditionField;
  value: any;
  onChange: (_v: any) => void;
}> = ({ field, value, onChange }) => {
  // Monthly values → 6-col grid
  if (field.type === 'number_array') {
    const arr: number[] = Array.isArray(value) && value.length > 0 ? value : DEFAULT_MONTHLY;
    const currMonth = new Date().getMonth();
    return (
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">{field.label}</label>
        {field.description && <p className="text-xs text-gray-500 mb-2">{field.description}</p>}
        <div className="grid grid-cols-6 gap-2">
          {arr.slice(0, 12).map((v: number, i: number) => (
            <div key={i} className="flex flex-col items-center">
              <span className="text-[10px] text-gray-500 font-medium mb-0.5">{MONTH_NAMES[(currMonth + i) % 12]}</span>
              <TextField
                type="number"
                value={v}
                onChange={e => { const next = [...arr]; next[i] = parseInt(e.target.value) || 0; onChange(next); }}
                size="small"
                inputProps={{ min: 0, style: { textAlign: 'center', padding: '6px 4px', fontSize: '0.85rem' } }}
                sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Select ≤3 options → radio buttons; otherwise dropdown
  if (field.type === 'select' && field.options) {
    if (field.options.length <= 3) {
      return (
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-0.5">{field.label}</label>
          {field.description && <p className="text-xs text-gray-500 mb-1">{field.description}</p>}
          <div className="flex gap-3">
            {field.options.map(opt => (
              <label key={opt.value} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name={field.key}
                  checked={String(value ?? field.default) === opt.value}
                  onChange={() => onChange(opt.value === 'true' ? true : opt.value === 'false' ? false : opt.value)}
                  className="text-blue-600"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">{field.label}</label>
        {field.description && <p className="text-xs text-gray-500 mb-1">{field.description}</p>}
        <select
          value={String(value ?? field.default)}
          onChange={e => { const v = e.target.value; onChange(v === 'true' ? true : v === 'false' ? false : v); }}
          className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-400 bg-white"
        >
          {field.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  // Scalar: number, percent, currency
  const prefix = field.type === 'currency' ? '$' : '';
  const suffix = field.type === 'percent' ? '%' : (field.unit || '');

  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">{field.label}</label>
      {field.description && <p className="text-xs text-gray-500 mb-1">{field.description}</p>}
      <div className="flex items-center gap-2">
        {prefix && <span className="text-sm text-gray-500">{prefix}</span>}
        <TextField
          type="number"
          value={value ?? field.default ?? 0}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          size="small"
          inputProps={{ step: field.type === 'currency' ? 1000 : 1, style: { textAlign: 'right' } }}
          sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
        />
        {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
      </div>
    </div>
  );
};

// ─── Condition Editor (primary + advanced) ───────────────────────────

export const ConditionEditor: React.FC<{
  schema: ConditionField[];
  values: Record<string, any>;
  onChange: (_values: Record<string, any>) => void;
}> = ({ schema, values, onChange }) => {
  const [showAdv, setShowAdv] = useState(false);
  const primary = schema.filter(f => !f.advanced);
  const advanced = schema.filter(f => f.advanced);

  const updateField = (key: string, v: any) => onChange({ ...values, [key]: v });

  return (
    <div className="space-y-5">
      {primary.map(f => (
        <ConditionFieldEditor key={f.key} field={f} value={values[f.key]} onChange={v => updateField(f.key, v)} />
      ))}
      {advanced.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowAdv(!showAdv)}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            {showAdv ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
            Advanced settings ({advanced.length})
          </button>
          <Collapse in={showAdv}>
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-4">
              {advanced.map(f => (
                <ConditionFieldEditor key={f.key} field={f} value={values[f.key]} onChange={v => updateField(f.key, v)} />
              ))}
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

// ─── Shared Rule Dialog ──────────────────────────────────────────────

export interface FormulaOption {
  id: number;
  code: string;
  name: string;
}

export interface RuleDialogProps {
  open: boolean;
  rule: Partial<{ id: number; name: string; rule_type: string; severity: string; formula_id: number | null; formula_code: string; enabled: boolean; cooldown_hours: number; condition: Record<string, any>; channels: Record<string, boolean> }> | null;
  onClose: () => void;
  onSave: (_rule: any) => void;
  isSaving: boolean;
  formulas: FormulaOption[];
  ruleTypes: RuleTypeMeta[];
  lockedRuleType?: string;
  lockedFormulaId?: number;
  conditionOnly?: boolean;
}

export const RuleDialog: React.FC<RuleDialogProps> = ({
  open, rule, onClose, onSave, isSaving, formulas, ruleTypes,
  lockedRuleType, lockedFormulaId, conditionOnly,
}) => {
  const isNew = !rule?.id;
  const effectiveRuleType = lockedRuleType || rule?.rule_type || '';

  const [name, setName] = useState(rule?.name || '');
  const [category, setCategory] = useState<string>('');
  const [ruleType, setRuleType] = useState(effectiveRuleType);
  const [severity, setSeverity] = useState(rule?.severity || 'warning');
  const [formulaId, setFormulaId] = useState<string>(
    lockedFormulaId ? String(lockedFormulaId) : (rule?.formula_id ? String(rule.formula_id) : ''),
  );
  const [enabled, setEnabled] = useState(rule?.enabled !== false);
  const [cooldownHours, setCooldownHours] = useState(rule?.cooldown_hours || 24);
  const [condition, setCondition] = useState<Record<string, any>>(rule?.condition || {});
  const [channels, setChannels] = useState<Record<string, boolean>>({
    dashboard: rule?.channels?.dashboard !== false,
    email: !!rule?.channels?.email,
    whatsapp: !!rule?.channels?.whatsapp,
  });

  const meta = useMemo(() => ruleTypes.find(rt => rt.ruleType === ruleType), [ruleTypes, ruleType]);

  // Categories available from rule types
  const categories = useMemo(() => {
    const cats = [...new Set(ruleTypes.map(rt => rt.category))];
    return cats.sort();
  }, [ruleTypes]);

  // Rule types filtered by selected category
  const filteredRuleTypes = useMemo(() => {
    if (!category) return ruleTypes;
    return ruleTypes.filter(rt => rt.category === category);
  }, [ruleTypes, category]);

  // Sync state when rule prop changes
  React.useEffect(() => {
    if (rule) {
      setName(rule.name || '');
      setRuleType(lockedRuleType || rule.rule_type || '');
      setSeverity(rule.severity || 'warning');
      setFormulaId(lockedFormulaId ? String(lockedFormulaId) : (rule.formula_id ? String(rule.formula_id) : ''));
      setEnabled(rule.enabled !== false);
      setCooldownHours(rule.cooldown_hours || 24);
      setCondition(rule.condition || {});
      setChannels({
        dashboard: rule.channels?.dashboard !== false,
        email: !!rule.channels?.email,
        whatsapp: !!rule.channels?.whatsapp,
      });
      // Pre-set category from existing rule
      const existingMeta = ruleTypes.find(rt => rt.ruleType === rule.rule_type);
      if (existingMeta) setCategory(existingMeta.category);
    } else {
      // Reset for new rule
      setCategory('');
      setRuleType('');
    }
  }, [rule, lockedRuleType, lockedFormulaId, ruleTypes]);

  // Seed defaults for new rules when type changes
  React.useEffect(() => {
    if (isNew && meta) {
      setCondition(prev => {
        const hasValues = Object.keys(prev).length > 0;
        return hasValues ? prev : { ...meta.defaultCondition };
      });
      if (!name && meta.label) setName(meta.label);
    }
  }, [ruleType, meta]);

  const handleSave = () => {
    if (conditionOnly && rule?.id) {
      onSave({ condition });
      return;
    }
    onSave({
      ...(isNew ? { category: meta?.category || category || 'strategy', rule_type: ruleType } : {}),
      name,
      severity,
      condition,
      formula_id: lockedFormulaId || (formulaId ? Number(formulaId) : null),
      enabled,
      cooldown_hours: cooldownHours,
      channels,
    });
  };

  // Which step are we on for new rules?
  const needsCategoryStep = isNew && !lockedRuleType && categories.length > 1 && !category;
  const needsPolicyStep = isNew && !lockedRuleType && category && !ruleType;
  const showDetails = !needsCategoryStep && !needsPolicyStep;

  const showFormulaPicker = !lockedFormulaId && !conditionOnly;
  const showAlertingSection = !conditionOnly;
  const showChannels = !conditionOnly;
  const showEnabled = !conditionOnly;
  const showNameField = !conditionOnly;

  // Dialog title
  let title = '';
  if (conditionOnly) title = meta?.label || 'Edit Condition';
  else if (needsCategoryStep) title = 'New Watch Rule';
  else if (needsPolicyStep) title = `${CATEGORY_META[category]?.label || category} Rules`;
  else if (isNew) title = meta?.label || 'Configure Rule';
  else title = meta?.label || rule?.name || 'Edit Rule';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', overflow: 'hidden' },
      }}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-5 pt-4 pb-3">
        {/* Back button for stepped flow */}
        {(needsPolicyStep || (showDetails && isNew && !lockedRuleType && category)) && (
          <IconButton
            size="small"
            onClick={() => {
              if (showDetails && ruleType) { setRuleType(''); setCondition({}); setName(''); }
              else { setCategory(''); setRuleType(''); }
            }}
            sx={{ mr: 0.5, color: '#6b7280' }}
          >
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-[15px] font-semibold text-gray-900 truncate">{title}</h2>
          {meta?.description && showDetails && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{meta.description}</p>
          )}
          {needsCategoryStep && (
            <p className="text-xs text-gray-500 mt-0.5">Choose a category to get started</p>
          )}
          {needsPolicyStep && (
            <p className="text-xs text-gray-500 mt-0.5">Select a rule type</p>
          )}
        </div>
        <IconButton onClick={onClose} size="small" sx={{ color: '#9ca3af', '&:hover': { color: '#6b7280' } }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </div>

      <DialogContent sx={{ px: 2.5, py: 0, pb: 2 }}>
        {/* ── Step 1: Category picker ── */}
        {needsCategoryStep && (
          <div className="grid gap-2 py-1">
            {categories.map(cat => {
              const cm = CATEGORY_META[cat] || { label: cat, color: '#6b7280', bg: '#f9fafb', desc: '' };
              const count = ruleTypes.filter(rt => rt.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => { setCategory(cat); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: cm.bg, color: cm.color }}
                  >
                    {cm.label[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 group-hover:text-gray-900">{cm.label}</div>
                    <div className="text-xs text-gray-500">{cm.desc}</div>
                  </div>
                  <span className="text-xs text-gray-400 tabular-nums">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Step 2: Policy type picker ── */}
        {needsPolicyStep && (
          <div className="grid gap-1.5 py-1">
            {filteredRuleTypes.map(rt => (
              <button
                key={rt.ruleType}
                onClick={() => { setRuleType(rt.ruleType); }}
                className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left group"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 group-hover:text-gray-900">{rt.label}</div>
                  {rt.description && <div className="text-xs text-gray-500 line-clamp-1">{rt.description}</div>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* ── Step 3 / Edit: Details ── */}
        {showDetails && (
          <div className="space-y-4 py-1">
            {/* Type badge (editing existing, not condition-only) */}
            {!isNew && !conditionOnly && meta && (
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                  style={{
                    backgroundColor: CATEGORY_META[meta.category]?.bg || '#f3f4f6',
                    color: CATEGORY_META[meta.category]?.color || '#6b7280',
                  }}
                >
                  {CATEGORY_META[meta.category]?.label || meta.category}
                </span>
                <span className="text-xs text-gray-400">{policyLabel(ruleType)}</span>
              </div>
            )}

            {/* Name */}
            {showNameField && (
              <div>
                <SectionLabel>Name</SectionLabel>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={meta?.label || 'Rule name'}
                  className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors bg-white placeholder:text-gray-400"
                />
              </div>
            )}

            {/* Formula picker */}
            {showFormulaPicker && (
              <div>
                <SectionLabel>Scope</SectionLabel>
                <select
                  value={formulaId}
                  onChange={e => setFormulaId(e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors bg-white"
                >
                  <option value="">Account-wide (all formulas)</option>
                  {formulas.map(f => (
                    <option key={f.id} value={String(f.id)}>{f.name} ({f.code})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Condition fields */}
            {meta?.conditionSchema && meta.conditionSchema.length > 0 && (
              <div className={conditionOnly ? '' : 'pt-1'}>
                {!conditionOnly && <SectionLabel>Condition</SectionLabel>}
                <div className="bg-gray-50 rounded-lg px-3 py-3">
                  <ConditionEditor
                    schema={meta.conditionSchema}
                    values={condition}
                    onChange={setCondition}
                  />
                </div>
              </div>
            )}

            {/* Severity + Cooldown */}
            {showAlertingSection && (
              <div className="pt-1">
                <SectionLabel>Alerting</SectionLabel>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <div className="flex gap-1.5">
                      {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSeverity(key)}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                            severity === key
                              ? 'border-current shadow-sm'
                              : 'border-transparent bg-gray-50 text-gray-500 hover:bg-gray-100'
                          }`}
                          style={severity === key ? { color: cfg.color, backgroundColor: cfg.bg } : undefined}
                        >
                          {cfg.icon} {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500">Cooldown</span>
                    <input
                      type="number"
                      value={cooldownHours}
                      onChange={e => setCooldownHours(Number(e.target.value))}
                      min={0}
                      className="w-14 text-sm text-right px-2 py-1.5 border border-gray-200 rounded-lg outline-none focus:border-blue-400 bg-white tabular-nums"
                    />
                    <span className="text-xs text-gray-400">h</span>
                  </div>
                </div>
              </div>
            )}

            {/* Channels */}
            {showChannels && (
              <div className="pt-1">
                <SectionLabel>Channels</SectionLabel>
                <div className="flex gap-2">
                  {CHANNEL_META.map(ch => (
                    <button
                      key={ch.key}
                      type="button"
                      onClick={() => setChannels(prev => ({ ...prev, [ch.key]: !prev[ch.key] }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                        channels[ch.key]
                          ? 'border-blue-200 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {ch.icon}
                      {ch.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Enabled toggle */}
            {showEnabled && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Enabled</span>
                <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} size="small" />
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      {showDetails && (
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f3f4f6' }}>
          <Button
            onClick={onClose}
            size="small"
            sx={{ color: '#6b7280', textTransform: 'none', fontWeight: 500, borderRadius: '8px', px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={isSaving || (!conditionOnly && !name)}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' },
            }}
          >
            {isSaving ? 'Saving\u2026' : isNew ? 'Create Rule' : 'Apply'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
